import React from 'react';
import {FontsData} from 'styled/theme';
import {Template} from '@guestapp/sdk';
import {useTimeoutRef} from '@guestapp/core';
import {useFrame} from 'context/frame';

async function getFontUrl(font: FontsData) {
  try {
    const downloadLink = font?.font_file_download_link as string;
    const response = await fetch(downloadLink);
    const blob = await response.blob();

    return {
      src: URL.createObjectURL(blob),
      name: font?.font_name,
      format: font?.font_format,
    };
  } catch (e) {
    console.error(e);
    return {};
  }
}

async function processFonts(fontsData: FontsData[]) {
  try {
    const fontBlobUrls = await Promise.all(fontsData.map(getFontUrl));
    let fontFaces = '';
    fontBlobUrls.forEach(fontBlobUrl => {
      const src = fontsData.find(
        font => font?.font_name === fontBlobUrl.name,
      )?.font_file_download_link;
      fontFaces += `
          @font-face {
            font-family: ${fontBlobUrl.name};
            src: url(${src}); format('${fontBlobUrl.format}');
          }
        `;
    });

    return fontFaces;
  } catch (error) {
    console.error('Error processing fonts:', error);
    return '';
  }
}

function injectFontsIntoDocument(fontFaces: string, document: Document) {
  const stylesElement = document.createElement('style');
  stylesElement.appendChild(document.createTextNode(fontFaces));
  document.getElementsByTagName('head')[0].appendChild(stylesElement);
}

type useCustomFontsProps = {
  onSuccess?: (data: {[key: string]: FontsData}) => void;
};

const defaultProps = {
  onSuccess: undefined,
};
function useCustomFonts({onSuccess}: useCustomFontsProps = defaultProps) {
  const timeoutRef = useTimeoutRef();
  const {document} = useFrame();

  const [fontsStatus, setFontsStatus] = React.useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const fetchFonts = React.useCallback(
    async (templateData: Template) => {
      const customFonts = [templateData?.titles_font, templateData?.body_font];
      setFontsStatus('loading');
      const builtFontFaces = await processFonts(customFonts);
      injectFontsIntoDocument(builtFontFaces, document);

      onSuccess?.({
        titles_font: templateData?.titles_font,
        body_font: templateData?.body_font,
      });
      timeoutRef.current = setTimeout(() => {
        setFontsStatus('success');
      }, 600);
    },
    [document, onSuccess, timeoutRef],
  );

  return {fetchFonts, fontsStatus};
}

export {useCustomFonts};
